import { useState, type JSX } from 'react';
import type { KitDemandForecastItemDto } from '@ctms/contracts';
import {
  AlertCircle,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Layers,
  PackageSearch,
} from 'lucide-react';

export interface KitDemandForecastTableProps {
  items: KitDemandForecastItemDto[];
  isLoading?: boolean;
}

/**
 * Forensic demand table (REQ-KIT-028, REQ-KIT-029). Rows with a shortage are
 * highlighted and expand to the visits whose need the virtual FEFO allocation
 * could not cover.
 */
export function KitDemandForecastTable({
  items,
  isLoading = false,
}: KitDemandForecastTableProps): JSX.Element {
  const [expandedKitIds, setExpandedKitIds] = useState<Set<number>>(new Set());

  function toggleExpand(labKitId: number): void {
    setExpandedKitIds((prev) => {
      const next = new Set(prev);
      if (next.has(labKitId)) {
        next.delete(labKitId);
      } else {
        next.add(labKitId);
      }
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border border-slate-800 bg-slate-900/50 p-8 text-slate-400">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
          <span className="text-sm">Liczenie prognozy zapotrzebowania...</span>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex h-48 flex-col items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-900/30 p-8 text-center text-slate-400">
        <PackageSearch className="h-10 w-10 text-slate-600 mb-2" />
        <p className="text-sm font-medium text-slate-300">Brak danych do prognozy</p>
        <p className="text-xs text-slate-500 max-w-md mt-1">
          Nie zarejestrowano zestawów w magazynie tego badania lub żaden zestaw nie pasuje do filtra
          laboratorium.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-md">
      <table
        className="w-full text-left text-sm text-slate-200"
        id="demand-forecast-table"
        data-testid="demand-forecast-table"
      >
        <thead className="border-b border-slate-800 bg-slate-950/80 text-xs font-semibold text-slate-400 uppercase tracking-wider">
          <tr>
            <th scope="col" className="w-10 px-4 py-3"></th>
            <th scope="col" className="px-4 py-3">Zestaw</th>
            <th scope="col" className="px-4 py-3">Laboratorium</th>
            <th scope="col" className="px-4 py-3 text-right">Zapas</th>
            <th scope="col" className="px-4 py-3 text-right">Wymagane</th>
            <th scope="col" className="px-4 py-3 text-right">Przydzielone</th>
            <th scope="col" className="px-4 py-3 text-right">Brak</th>
            <th scope="col" className="px-4 py-3 text-right">Za ile dni</th>
            <th scope="col" className="px-4 py-3 text-right">Niezaplanowane</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {items.map((item) => {
            const isExpanded = expandedKitIds.has(item.labKitId);
            const hasShortageVisits = item.shortageVisits.length > 0;

            return (
              <tr
                key={item.labKitId}
                data-testid={`forecast-row-${item.labKitId}`}
                className={
                  item.hasShortage
                    ? 'bg-rose-950/20 hover:bg-rose-950/30 transition-colors'
                    : 'hover:bg-slate-800/40 transition-colors'
                }
              >
                <td colSpan={9} className="p-0">
                  <div
                    onClick={() => hasShortageVisits && toggleExpand(item.labKitId)}
                    className={`flex items-center w-full px-4 py-3.5 select-none ${
                      hasShortageVisits ? 'cursor-pointer' : ''
                    }`}
                  >
                    <div className="w-10 shrink-0">
                      {hasShortageVisits ? (
                        <button
                          type="button"
                          aria-label={isExpanded ? 'Zwiń wizyty' : 'Rozwiń wizyty'}
                          className="text-slate-400 hover:text-rose-400"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      ) : (
                        <span className="w-4 h-4 inline-block" />
                      )}
                    </div>

                    <div className="flex-1 font-medium text-slate-100 flex items-center gap-2">
                      <span>{item.kitName}</span>
                      {item.hasShortage && (
                        <span
                          data-testid={`forecast-shortage-${item.labKitId}`}
                          title="Wirtualne wydanie FEFO nie pokrywa zapotrzebowania (REQ-KIT-028)"
                          className="inline-flex items-center gap-1 rounded-sm bg-rose-950/80 border border-rose-800/60 px-2 py-0.5 text-[11px] font-semibold text-rose-300"
                        >
                          <AlertCircle className="h-3 w-3" />
                          <span>−{item.shortageKits} szt.</span>
                        </span>
                      )}
                    </div>

                    <div className="w-44 text-xs text-slate-300 truncate">
                      {item.laboratoryName}
                    </div>

                    <div className="w-24 text-right font-mono text-sm text-slate-200">
                      {item.totalStock}
                    </div>

                    <div className="w-24 text-right font-mono text-sm text-slate-300">
                      {item.requiredKits}
                    </div>

                    <div className="w-28 text-right font-mono text-sm text-cyan-300">
                      {item.allocatedKits}
                    </div>

                    <div className="w-24 text-right">
                      <span
                        data-testid={`forecast-shortage-value-${item.labKitId}`}
                        className={`inline-block font-mono text-sm font-semibold px-2 py-0.5 rounded-sm ${
                          item.hasShortage
                            ? 'text-rose-300 bg-rose-950/40'
                            : 'text-slate-500 bg-slate-800/30'
                        }`}
                      >
                        {item.shortageKits}
                      </span>
                    </div>

                    <div
                      className="w-28 text-right text-xs"
                      data-testid={`forecast-days-${item.labKitId}`}
                    >
                      {item.daysUntilShortage === null ? (
                        <span className="text-slate-500 inline-flex items-center gap-1">
                          {item.hasShortage ? (
                            <>
                              <CalendarClock className="h-3.5 w-3.5" />
                              <span>wkótce</span>
                            </>
                          ) : (
                            <>
                              <CircleCheck className="h-3.5 w-3.5 text-emerald-500" />
                              <span>pokryte</span>
                            </>
                          )}
                        </span>
                      ) : (
                        <span className="text-amber-300 font-medium inline-flex items-center gap-1">
                          <CalendarClock className="h-3.5 w-3.5" />
                          <span>za {item.daysUntilShortage} dni</span>
                        </span>
                      )}
                    </div>

                    <div className="w-32 text-right text-xs text-slate-400 font-mono">
                      {item.unscheduledVisits > 0 ? `${item.unscheduledVisits}` : '—'}
                    </div>
                  </div>

                  {/* Accordion: visits that cannot be covered (REQ-KIT-028/029) */}
                  {isExpanded && hasShortageVisits && (
                    <div className="bg-slate-950/70 border-t border-slate-800 px-10 py-3">
                      <div className="rounded-lg border border-slate-800/80 bg-slate-900/90 overflow-hidden">
                        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2 bg-slate-950/50 text-xs font-medium text-slate-400">
                          <span className="flex items-center gap-1.5 text-rose-400">
                            <Layers className="h-3.5 w-3.5" />
                            <span>Wizyty bez pokrycia (najbliższe pierwsze)</span>
                          </span>
                          <span className="text-[11px] text-slate-500">REQ-KIT-028, REQ-KIT-029</span>
                        </div>

                        <table className="w-full text-xs text-left">
                          <thead className="border-b border-slate-800 bg-slate-950/40 text-[11px] text-slate-400 font-semibold uppercase">
                            <tr>
                              <th className="px-4 py-2">Wizyta</th>
                              <th className="px-4 py-2">Pacjent</th>
                              <th className="px-4 py-2">Data</th>
                              <th className="px-4 py-2 text-right">Za ile dni</th>
                              <th className="px-4 py-2 text-right">Brakuje</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/60">
                            {item.shortageVisits.map((visit) => (
                              <tr
                                key={visit.patientVisitId}
                                data-testid={`forecast-visit-${visit.patientVisitId}`}
                                className="text-slate-300"
                              >
                                <td className="px-4 py-2.5">{visit.visitName}</td>
                                <td className="px-4 py-2.5">
                                  {visit.patientName}{' '}
                                  <span className="text-slate-500">#{visit.patientId}</span>
                                </td>
                                <td className="px-4 py-2.5 font-mono">
                                  {visit.dateScheduled ?? 'niezaplanowana'}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono">
                                  {visit.daysUntil === null ? '—' : `${visit.daysUntil}`}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono font-semibold text-rose-300">
                                  −{visit.kitsShort} szt.
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}